import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { HealthAndSafety, History, Info, Refresh } from '@mui/icons-material';
import { dhis2SyncAPI, type Dhis2ConfigOut } from '../../services/api';

interface SyncHealthDashboardProps {
  configs: Dhis2ConfigOut[];
}

export default function SyncHealthDashboard({ configs }: SyncHealthDashboardProps) {
  const [health, setHealth] = useState<{ total: number; success: number; failed: number; success_rate_pct: number; alert?: boolean; alert_message?: string } | null>(null);
  const [entries, setEntries] = useState<Array<{ id: string; config_id: string; session_id: string | null; action: string; status_code: number | null; event_id: string | null; error_message: string | null; created_at: string | null }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterConfigId, setFilterConfigId] = useState<string>('');
  const [hours, setHours] = useState(24);

  const loadHealth = async () => {
    try {
      const res = await dhis2SyncAPI.health({ hours });
      setHealth(res.data);
    } catch {
      setHealth(null);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await dhis2SyncAPI.history({
        config_id: filterConfigId || undefined,
        limit: 100,
      });
      setEntries(res.data?.entries ?? []);
    } catch {
      setEntries([]);
    }
  };

  const loadAll = () => {
    setLoading(true);
    setError(null);
    Promise.all([loadHealth(), loadHistory()]).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
  }, [filterConfigId, hours]);

  const configNameById = (id: string) => configs.find((c) => c.id === id)?.name ?? id.slice(0, 8);

  if (loading && !health && entries.length === 0) {
    return <Typography>Loading…</Typography>;
  }

  return (
    <Box>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Sync Health (last {hours}h) and Sync History. Data from dhis2_sync_log.
      </Typography>
      <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom><strong>How sync works</strong></Typography>
        <Typography variant="body2">
          Sync is <strong>not</strong> a background process. Events are sent to DHIS2 when you trigger a push from the <strong>Consultation</strong> flow
          (e.g. after completing a session, when you confirm &quot;Sync to DHIS2&quot; or use the sync action). Each push is logged here.
          Use the filters below to see success rate and history; click <strong>Refresh</strong> to reload.
        </Typography>
      </Alert>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {health?.alert && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {health.alert_message ?? 'Sync success rate is below configured threshold. Check Sync History for failures.'}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Period</InputLabel>
          <Select value={hours} label="Period" onChange={(e) => setHours(Number(e.target.value))}>
            <MenuItem value={1}>Last 1 hour</MenuItem>
            <MenuItem value={24}>Last 24 hours</MenuItem>
            <MenuItem value={72}>Last 72 hours</MenuItem>
            <MenuItem value={168}>Last 7 days</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Config filter</InputLabel>
          <Select value={filterConfigId} label="Config filter" onChange={(e) => setFilterConfigId(e.target.value)}>
            <MenuItem value="">All configs</MenuItem>
            {configs.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={loadAll} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Box>

      {health && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HealthAndSafety /> Sync Health
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Chip label={`Success rate: ${health.success_rate_pct}%`} color={health.success_rate_pct >= 95 ? 'success' : health.success_rate_pct >= 80 ? 'warning' : 'error'} />
            <Chip label={`Total: ${health.total}`} variant="outlined" />
            <Chip label={`Success: ${health.success}`} color="success" variant="outlined" />
            <Chip label={`Failed: ${health.failed}`} color="error" variant="outlined" />
          </Box>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Typography variant="subtitle2" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <History /> Sync History
        </Typography>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Config</TableCell>
                <TableCell>Session</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Event ID</TableCell>
                <TableCell>Error</TableCell>
                <TableCell>Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>No sync entries yet. Use DHIS2 sync push to send events.</TableCell>
                </TableRow>
              ) : (
                entries.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell>{configNameById(e.config_id)}</TableCell>
                    <TableCell>{e.session_id ? e.session_id.slice(0, 8) + '…' : '—'}</TableCell>
                    <TableCell>{e.action}</TableCell>
                    <TableCell>
                      {e.status_code != null ? (
                        <Chip size="small" label={e.status_code} color={e.status_code >= 200 && e.status_code < 300 ? 'success' : 'error'} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 120 }}>{e.event_id ?? '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 200 }} title={e.error_message ?? ''}>
                      {e.error_message ? e.error_message.slice(0, 50) + (e.error_message.length > 50 ? '…' : '') : '—'}
                    </TableCell>
                    <TableCell>{e.created_at ? new Date(e.created_at).toLocaleString() : '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
